import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import './profile.scss';
import {
  Engagement, ProfileData, recordingKindLabel, todayString, Work, workStatusLabel, WorkStatus,
} from '../mypage/profileTypes';

interface PublicProfileResponse {
  name: string;
  isOwner: boolean;
  profile: ProfileData;
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// 2026-11-14 → { md: '11.14', year: '2026', weekday: 'SAT' }
function splitDate(date: string) {
  const [y, m, d] = date.split('-').map(Number);
  return {
    md: date.slice(5).replace('-', '.'),
    year: String(y),
    weekday: WEEKDAYS[new Date(y, m - 1, d).getDay()],
  };
}

// 작곡가별로 묶되, 처음 입력한 순서를 유지
function groupByComposer(works: Work[]) {
  const groups: { composer: string; works: Work[] }[] = [];
  works.forEach((w) => {
    const group = groups.find((g) => g.composer === w.composer);
    if (group) group.works.push(w);
    else groups.push({ composer: w.composer, works: [w] });
  });
  return groups;
}

function groupByYear(engagements: Engagement[]) {
  const years: { year: string; events: Engagement[] }[] = [];
  engagements.forEach((e) => {
    const year = e.date.slice(0, 4);
    const group = years.find((y) => y.year === year);
    if (group) group.events.push(e);
    else years.push({ year, events: [e] });
  });
  return years;
}

function BrandMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M3 19c0-6.6 4.5-12 10-12s10 5.4 10 12" stroke="#0B0B0A" strokeWidth="1.2" />
      <path d="M13 7V3" stroke="#0B0B0A" strokeWidth="1.2" />
      <circle cx="13" cy="20.5" r="2.4" fill="#D9902A" />
    </svg>
  );
}

function TopBar({ isOwner }: { isOwner: boolean }) {
  return (
    <header className="topbar">
      <Link className="brand" to="/">
        <BrandMark />
        <span className="brand-txt">
          <span className="brand-kr">클래시커</span>
          <span className="brand-lat">CLASSICKER</span>
        </span>
      </Link>
      <div className="top-right">
        {isOwner
          ? <Link className="btn btn-ghost" to="/mypage">마이페이지</Link>
          : <Link className="btn btn-ghost" to="/intro">클래시커 알아보기</Link>}
      </div>
    </header>
  );
}

export default function PublicProfile() {

  const { handle = '' } = useParams();
  const [data, setData] = useState<PublicProfileResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [activeFilter, setActiveFilter] = useState<WorkStatus | null>(null);
  const [activeSection, setActiveSection] = useState('repertoire');
  const [copyMsg, setCopyMsg] = useState('');

  useEffect(() => {
    setData(null);
    setNotFound(false);
    axios.get(`/api/profile/u/${encodeURIComponent(handle)}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true));
  }, [handle]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyMsg('링크를 복사했습니다');
    } catch {
      setCopyMsg('주소창의 링크를 복사해 주세요');
    }
  };

  if (notFound) {
    return (
      <div className="profile-page">
        <div className="frame">
          <TopBar isOwner={false} />
          <section className="block" style={{ padding: '80px 0' }}>
            <div className="sec-head"><h2>프로필을 찾을 수 없습니다</h2></div>
            <p className="sec-desc">주소가 바뀌었거나 비공개로 설정된 프로필일 수 있습니다.</p>
            <Link className="btn btn-primary" to="/">클래시커 홈으로</Link>
          </section>
        </div>
      </div>
    );
  }

  if (!data) return <div className="profile-page" />;

  const { name, isOwner, profile } = data;
  const today = todayString();
  const upcoming = profile.engagements.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = profile.engagements.filter((e) => e.date < today);
  const filteredWorks = profile.works.filter((w) => !activeFilter || w.status === activeFilter);
  const composerGroups = groupByComposer(filteredWorks);
  const availLabels = [
    profile.availConcerto && '협연',
    profile.availChamber && '실내악',
    profile.availLesson && '레슨',
  ].filter(Boolean) as string[];
  const facts = [
    { dt: '전공', dd: profile.instrument },
    { dt: '사사', dd: profile.teachers },
    { dt: '악기', dd: profile.instrumentDetail },
    { dt: '소속', dd: profile.ensemble },
  ].filter((f) => f.dd);
  const hasAbout = !!profile.bio || facts.length > 0;

  const sections = [
    { id: 'repertoire', label: '레퍼토리' },
    { id: 'history', label: '연주 이력' },
    ...(profile.recordings.length ? [{ id: 'media', label: '녹음·영상' }] : []),
    ...(hasAbout ? [{ id: 'about', label: '소개' }] : []),
  ];
  const secNum = (id: string) => String(sections.findIndex((s) => s.id === id) + 1).padStart(2, '0');

  return (
    <div className="profile-page">
      <div className="frame">

        <TopBar isOwner={isOwner} />

        {/* ---------------- hero ---------------- */}
        <section className="hero">
          <div>
            <p className="eyebrow">{[profile.instrument, profile.ensemble].filter(Boolean).join(' · ')}</p>
            <h1>{name}</h1>
            {profile.nameLat && <p className="name-lat">{profile.nameLat.toUpperCase()}</p>}
            {profile.tagline && <p className="tagline">{profile.tagline}</p>}
            <div className="meta">
              <span>classicker.com/{profile.handle}</span>
              {profile.availRegion && <span>{profile.availRegion}</span>}
              {isOwner && profile.visibility === 'private' && <span>나만 보기 · 다른 사람에게는 보이지 않습니다</span>}
            </div>
            <div className="actions">
              <button className="btn btn-primary" type="button" onClick={copyLink}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 5l3-3 3 3" stroke="currentColor" strokeWidth="1.1" /><path d="M3 9v4h10V9" stroke="currentColor" strokeWidth="1.1" /></svg>
                링크 복사
              </button>
              {isOwner && <Link className="btn btn-ghost" to="/profile/edit">프로필 수정</Link>}
            </div>
            {copyMsg && <p className="updated" style={{ textAlign: 'left', marginTop: 10 }} role="status">{copyMsg}</p>}
          </div>

          <div className="season">
            <div className="season-head"><span>이번 시즌</span><b>{upcoming.length}건</b></div>
            {upcoming.length === 0 && <p className="empty">예정된 무대가 아직 없습니다.</p>}
            {upcoming.slice(0, 3).map((gig, i) => {
              const d = splitDate(gig.date);
              return (
                <div className="gig" key={`${gig.date}-${i}`}>
                  <span className="gig-date">{d.md}<em>{d.year} {d.weekday}</em></span>
                  <span>
                    <span className="gig-work">{gig.title}</span>
                    <span className="gig-venue">{[gig.venue, gig.role].filter(Boolean).join(' · ')}</span>
                  </span>
                </div>
              );
            })}
            {upcoming.length > 3 && (
              <a className="season-more" href="#history" onClick={() => setActiveSection('history')}>전체 일정 보기 →</a>
            )}
          </div>
        </section>

        {/* ---------------- sub nav ---------------- */}
        <nav className="subnav">
          {sections.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              aria-current={activeSection === item.id ? 'page' : undefined}
              onClick={() => setActiveSection(item.id)}
            >
              {item.label}
            </a>
          ))}
          {availLabels.length > 0 && (
            <span className="right"><span className="status-pill"><i></i>{availLabels.join('·')} 가능</span></span>
          )}
        </nav>

        <div className="cols">
          <div>

            {/* ---------------- repertoire ---------------- */}
            <section className="block" id="repertoire">
              <div className="sec-head">
                <span className="sec-num">{secNum('repertoire')}</span>
                <h2>레퍼토리</h2>
                <span className="sec-count">{profile.works.length}곡</span>
              </div>

              {profile.works.length > 0 && (
                <div className="filters">
                  <button className="chip" type="button" aria-pressed={activeFilter === null} onClick={() => setActiveFilter(null)}>
                    전체 {profile.works.length}
                  </button>
                  {(Object.keys(workStatusLabel) as WorkStatus[]).map((s) => (
                    <button key={s} className="chip" type="button" aria-pressed={activeFilter === s} onClick={() => setActiveFilter(s)}>
                      {workStatusLabel[s]} {profile.works.filter((w) => w.status === s).length}
                    </button>
                  ))}
                </div>
              )}

              {composerGroups.length === 0 && (
                <p className="empty">{profile.works.length ? '이 분류에 공개된 곡이 아직 없습니다.' : '아직 등록된 곡이 없습니다.'}</p>
              )}

              {composerGroups.map((group) => (
                <div className="composer" key={group.composer}>
                  <div className="composer-head">
                    <span className="composer-name">{group.composer}</span>
                    <span className="composer-n">{group.works.length}곡</span>
                  </div>
                  {group.works.map((work, i) => (
                    <div className="work" key={`${work.catalog}-${work.title}-${i}`}>
                      <div className="work-cat">{work.catalog || '—'}</div>
                      <div>
                        <div className="work-title">{work.title}</div>
                      </div>
                      <div className="work-tags">
                        <span className={`tag t-${work.status}`}>{workStatusLabel[work.status]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </section>

            {/* ---------------- history ---------------- */}
            <section className="block" id="history">
              <div className="sec-head">
                <span className="sec-num">{secNum('history')}</span>
                <h2>연주 이력</h2>
                <span className="sec-count">{past.length}회</span>
              </div>

              {upcoming.length > 3 && (
                <div className="tl">
                  <div className="tl-year">예정<small>{upcoming.length}건</small></div>
                  {upcoming.map((evt, i) => {
                    const d = splitDate(evt.date);
                    return (
                      <div className="evt" key={`up-${evt.date}-${i}`}>
                        <div className="evt-date">{d.md}<em>{d.year} {d.weekday}</em></div>
                        <div>
                          <div className="evt-title">{evt.title}</div>
                          <div className="evt-meta">{evt.venue}{evt.role && <> · <b>{evt.role}</b></>}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {past.length === 0 && <p className="empty">아직 등록된 연주 이력이 없습니다.</p>}

              <div className="tl">
                {groupByYear(past).map((y) => (
                  <React.Fragment key={y.year}>
                    <div className="tl-year">{y.year}<small>{y.events.length}회</small></div>
                    {y.events.map((evt, i) => {
                      const d = splitDate(evt.date);
                      return (
                        <div className="evt" key={`${evt.date}-${i}`}>
                          <div className="evt-date">{d.md}<em>{d.weekday}</em></div>
                          <div>
                            <div className="evt-title">{evt.title}</div>
                            <div className="evt-meta">{evt.venue}{evt.role && <> · <b>{evt.role}</b></>}</div>
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </section>

            {/* ---------------- media ---------------- */}
            {profile.recordings.length > 0 && (
              <section className="block" id="media">
                <div className="sec-head">
                  <span className="sec-num">{secNum('media')}</span>
                  <h2>녹음 · 영상</h2>
                  <span className="sec-count">{profile.recordings.length}개</span>
                </div>
                <p className="sec-desc">프로그램을 보내기 전에, 소리를 먼저 확인할 수 있게.</p>

                <div className="media-grid">
                  {profile.recordings.map((m, i) => (
                    <a className="media" key={`${m.url}-${i}`} href={m.url} target="_blank" rel="noopener noreferrer">
                      <span className="play"><svg width="11" height="12" viewBox="0 0 11 12" fill="none"><path d="M1 1l9 5-9 5z" fill="currentColor" /></svg></span>
                      <div>
                        <div className="media-title">{m.title}</div>
                        <div className="media-sub">
                          {[m.date && m.date.replace(/-/g, '.'), recordingKindLabel[m.kind]].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <span className="media-time">{m.duration}</span>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* ---------------- about ---------------- */}
            {hasAbout && (
              <section className="block" id="about">
                <div className="sec-head">
                  <span className="sec-num">{secNum('about')}</span>
                  <h2>소개</h2>
                </div>
                {profile.bio && (
                  <div className="about-text">
                    {profile.bio.split(/\n+/).filter((p) => p.trim()).map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                )}
                {facts.length > 0 && (
                  <dl className="facts">
                    {facts.map((f) => (
                      <div key={f.dt}><dt>{f.dt}</dt><dd>{f.dd}</dd></div>
                    ))}
                  </dl>
                )}
              </section>
            )}

          </div>

          {/* ---------------- aside ---------------- */}
          <aside>
            <div className="card">
              <div className="card-title">
                지금 연주 가능{profile.availFrom && <b>{profile.availFrom.replace('-', '.')} —</b>}
              </div>
              <div className="avail">
                {[
                  { label: '협연', on: profile.availConcerto },
                  { label: '실내악', on: profile.availChamber },
                  { label: '레슨', on: profile.availLesson },
                ].map((a) => (
                  <div className="avail-row" key={a.label}>
                    <span>{a.label}</span>
                    <span className={a.on ? 'badge-on' : 'badge-off'}>{a.on ? '가능' : '불가'}</span>
                  </div>
                ))}
              </div>
              {profile.availRegion && <p className="note">{profile.availRegion}</p>}
            </div>

            {profile.ensemble && (
              <div className="card">
                <div className="card-title">소속 앙상블</div>
                <div className="ens">
                  <div className="ens-item">
                    <span className="ens-name">{profile.ensemble}</span>
                    <span className="ens-role">{profile.instrument}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-title">프로필 공유</div>
              <button className="btn btn-primary" type="button" onClick={copyLink}>링크 복사</button>
              {profile.updatedAt && (
                <p className="updated">{profile.updatedAt.slice(0, 10).replace(/-/g, '.')} 업데이트</p>
              )}
            </div>
          </aside>
        </div>

        <footer className="foot">
          <span><b>클래시커</b> — 클래식을 연주하고 듣는 사람들의 프로필</span>
          <span>classicker.com/{profile.handle}</span>
        </footer>

      </div>
    </div>
  );
}
